import { supabase } from '@/lib/supabase';
import type { Staff } from '@/lib/types';

/**
 * Client helpers for the publish-event reminder slice (0097).
 *
 * - getMyStaffRecord: fetches the staff row linked to the signed-in profile
 *   so the prefs page can render the personal calendar URL.
 * - rotateCalendarToken: hits public.staff_rotate_calendar_token() to mint
 *   a fresh token; the old URL stops working immediately.
 * - calendarFeedUrl: assembles the public .ics URL from a token.
 * - downloadCurrentMonthIcs: triggers a one-time .ics snapshot download
 *   via the same /cal endpoint -- the calendar feed is the same generator
 *   for both subscribe and download paths.
 * - sendSchedulePublishEmail: fires the send-schedule-publish-email Edge
 *   Function after a successful publishSchedule() call.
 */

const CALENDAR_FEED_BASE =
  (import.meta.env.VITE_APP_URL as string | undefined) ?? window.location.origin;

export function calendarFeedUrl(token: string): string {
  return `${CALENDAR_FEED_BASE.replace(/\/$/, '')}/cal/${token}.ics`;
}

export async function getMyStaffRecord(): Promise<Staff | null> {
  const { data: userRes } = await supabase.auth.getUser();
  const uid = userRes.user?.id;
  if (!uid) return null;
  const { data, error } = await supabase
    .from('staff')
    .select('*')
    .eq('profile_id', uid)
    .maybeSingle();
  if (error) throw error;
  return (data as Staff | null) ?? null;
}

export async function rotateCalendarToken(staffId: string): Promise<Staff> {
  const { data, error } = await supabase.rpc('staff_rotate_calendar_token', {
    p_staff_id: staffId,
  });
  if (error) throw error;
  return data as Staff;
}

/**
 * Trigger a fresh .ics download. We use the same /cal feed URL the user
 * could subscribe to -- forcing the browser to save it as a file via the
 * `download` attribute on a temporary anchor element.
 */
export function downloadCurrentMonthIcs(token: string, filename = 'pharmasync.ics'): void {
  const url = calendarFeedUrl(token);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * Result shape returned by the send-schedule-publish-email Edge Function.
 */
export interface SchedulePublishEmailResult {
  sent: number;
  failed?: number;
  skipped_already_sent: number;
  skipped_no_email: number;
  message?: string;
  errors?: Array<{ staff_id: string; error: string }>;
}

/**
 * Fan out the "your schedule is ready" email after a successful publish.
 * Never throws -- callers can safely fire-and-forget. Errors are logged to
 * the console; the audit_events table captures detail server-side.
 */
export async function sendSchedulePublishEmail(
  scheduleId: string
): Promise<SchedulePublishEmailResult | null> {
  try {
    const { data, error } = await supabase.functions.invoke(
      'send-schedule-publish-email',
      { body: { schedule_id: scheduleId } }
    );
    if (error) {
      // non-critical: publish-event email is fire-and-forget. Idempotency log
      // prevents double-send, so user can re-publish to retry the fan-out.
      console.warn('send-schedule-publish-email failed:', error.message);
      return null;
    }
    return data as SchedulePublishEmailResult;
  } catch (err) {
    // non-critical: see comment above.
    console.warn('send-schedule-publish-email threw:', err);
    return null;
  }
}
